import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { BarChart3, TrendingUp, DollarSign, ArrowUpRight, Users, Calendar, Target, Sparkles, ChevronRight, Activity, PieChart, UserCheck, Heart, ClipboardList, Star } from "lucide-react";
import { Badge } from "../../ui/badge";

const navItems = [
  { label: "Daily Huddle", icon: Sparkles, active: false },
  { label: "Production & Collections", icon: DollarSign, active: false },
  { label: "Collections Summary", icon: PieChart, active: false },
  { label: "Adjustments", icon: ClipboardList, active: false },
  { label: "A/R Aging", icon: Target, active: false },
  { label: "Case Acceptance", icon: Heart, active: false },
  { label: "Hygiene Metrics", icon: Activity, active: false },
  { label: "Patient Flow", icon: Users, active: false },
  { label: "Scheduling", icon: Calendar, active: false },
  { label: "Provider Scorecard", icon: Star, active: false },
];

const views = [
  {
    nav: "Daily Huddle",
    title: "Daily Huddle",
    kpis: [
      { label: "Today's Production", value: "$0.00", sub: "5 patients on schedule" },
      { label: "MTD Gross Production", value: "$1,023,690", sub: "102% of goal" },
      { label: "MTD Net Production", value: "$1,023,890", sub: "133% adjustment ratio" },
    ],
    insights: [
      { emoji: "green", title: "MTD Production is Outstanding", detail: "$1,023,690 — 102% of monthly goal of $200,000" },
      { emoji: "amber", title: "Yesterday's Collections Missed Target", detail: "Let's focus on capturing today's opportunities to stay on track" },
      { emoji: "blue", title: "Verify Insurance for Tomorrow's Patients", detail: "3 patients scheduled tomorrow need verification" },
    ],
  },
  {
    nav: "Production & Collections",
    title: "Production & Collections",
    kpis: [
      { label: "Scheduled Production", value: "$1,048,525", sub: "This month" },
      { label: "Gross Production", value: "$1,023,690", sub: "Actual" },
      { label: "Net Production", value: "$1,023,890", sub: "After adjustments" },
      { label: "Net Collections", value: "$45,107", sub: "4.4% collection rate" },
    ],
    chart: true,
  },
  {
    nav: "Case Acceptance",
    title: "Case Acceptance",
    kpis: [
      { label: "Acceptance Rate", value: "64.7%", sub: "Last 30 days" },
      { label: "Diagnosed Value", value: "$705,813", sub: "Total diagnosed" },
      { label: "Accepted Value", value: "$676,550", sub: "Total accepted" },
      { label: "Unscheduled", value: "$674,215", sub: "6.3% diagnosis rate" },
    ],
    funnel: true,
  },
  {
    nav: "Provider Scorecard",
    title: "Provider Scorecard",
    kpis: [
      { label: "Production/Collection", value: "$475,969", sub: "Month to date" },
      { label: "Collection Rate", value: "177.2%", sub: "Above benchmark" },
      { label: "Avg Production/Visit", value: "$1,356", sub: "Per visit" },
      { label: "Case Acceptance", value: "54.8%", sub: "Trending up" },
    ],
    insights: [
      { emoji: "green", title: "Exceptional Collections Performance", detail: "108.6% collection rate — significantly exceeding industry standard" },
      { emoji: "green", title: "High Production Per Hour by Key Providers", detail: "$732.55 avg, well above practice average of $651.58" },
      { emoji: "blue", title: "Case Acceptance Trending Toward Growth", detail: "54.8% — with room to grow toward 60-70% industry typical range" },
    ],
  },
];

const PMSRevenueAnalyticsSlide = () => {
  const [activeView, setActiveView] = useState(0);

  useEffect(() => {
    const interval = setInterval(() => {
      setActiveView((prev) => (prev + 1) % views.length);
    }, 5000);
    return () => clearInterval(interval);
  }, []);

  const view = views[activeView];

  return (
    <div className="h-full w-full bg-slate-50 flex items-center justify-center p-12 relative overflow-hidden">
      <div className="absolute top-0 left-0 w-full h-full overflow-hidden z-0">
        <div className="absolute top-[-10%] right-[-5%] w-[600px] h-[600px] bg-blue-100 rounded-full blur-[100px] opacity-60" />
        <div className="absolute bottom-[-10%] left-[-5%] w-[600px] h-[600px] bg-indigo-100 rounded-full blur-[100px] opacity-60" />
      </div>

      <div className="flex flex-row h-full w-full max-w-[1600px] gap-10 relative z-10">
        {/* Left Side */}
        <div className="w-[38%] flex flex-col justify-center space-y-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-blue-100 text-blue-700 text-sm font-bold border border-blue-200 mb-4">
              <BarChart3 className="w-4 h-4" />
              <span>PMS Feature</span>
            </div>

            <h2 className="text-4xl lg:text-5xl font-bold text-slate-900 leading-tight mb-4">
              AI Revenue{" "}
              <span className="text-blue-600">Analytics Suite</span>
            </h2>

            <p className="text-lg text-slate-600 leading-relaxed mb-6">
              Real-time practice intelligence with AI-powered insights. Track production,
              collections, case acceptance, A/R aging, hygiene metrics, and provider
              performance — with actionable recommendations to grow revenue.
            </p>
          </motion.div>

          <div className="flex flex-wrap gap-2">
            {[
              "Daily Huddle",
              "AI Insights",
              "Provider Scorecard",
              "Case Acceptance",
              "A/R Aging",
              "Hygiene Metrics",
              "Patient Flow",
              "Goal Tracking",
            ].map((f, i) => (
              <motion.div
                key={f}
                initial={{ opacity: 0, scale: 0.8 }}
                whileInView={{ opacity: 1, scale: 1 }}
                transition={{ delay: 0.3 + i * 0.06, duration: 0.4 }}
                className="px-3 py-1.5 bg-white rounded-full text-sm font-medium text-slate-700 border border-slate-200 shadow-sm"
              >
                {f}
              </motion.div>
            ))}
          </div>

          {/* Replaces Dental Intel callout */}
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.6 }}
            className="bg-white p-4 rounded-xl border border-blue-200 shadow-sm"
          >
            <div className="flex items-center gap-2 mb-2">
              <div className="w-5 h-5 bg-blue-600 rounded-full flex items-center justify-center">
                <TrendingUp className="w-3 h-3 text-white" />
              </div>
              <span className="text-xs font-bold text-blue-700 uppercase tracking-wide">Replaces Dental Intel & more</span>
            </div>
            <p className="text-sm text-slate-600 leading-relaxed">
              Production tracking, collections analysis, scheduling efficiency, provider
              scorecards, and goal setting — all with AI insights built in.
            </p>
          </motion.div>
        </div>

        {/* Right Side: Analytics Dashboard */}
        <div className="w-[62%] h-full flex items-center justify-center relative">
          <motion.div
            initial={{ y: 40, opacity: 0 }}
            whileInView={{ y: 0, opacity: 1 }}
            transition={{ duration: 0.7, ease: "easeOut" }}
            className="w-full max-w-4xl bg-white rounded-2xl shadow-2xl border border-slate-200 overflow-hidden flex flex-row h-[600px] relative z-10"
          >
            {/* Sidebar Nav */}
            <div className="w-[180px] bg-slate-50 border-r border-slate-100 flex flex-col py-3 flex-shrink-0">
              <div className="px-4 pb-3 mb-1 border-b border-slate-100">
                <div className="flex items-center gap-2">
                  <div className="w-6 h-6 bg-blue-600 rounded-lg flex items-center justify-center">
                    <span className="text-white text-[10px] font-bold">A</span>
                  </div>
                  <span className="text-xs font-bold text-slate-700">Reports & Analytics</span>
                </div>
              </div>
              <div className="space-y-0.5 px-2 overflow-y-auto flex-1">
                {navItems.map((item, i) => {
                  const isActive = view.nav === item.label;
                  const Icon = item.icon;
                  return (
                    <button
                      key={i}
                      onClick={() => {
                        const idx = views.findIndex(v => v.nav === item.label);
                        if (idx !== -1) setActiveView(idx);
                      }}
                      className={`w-full flex items-center gap-2 px-2.5 py-1.5 rounded-lg text-left transition-colors ${
                        isActive
                          ? "bg-blue-50 text-blue-700"
                          : "text-slate-500 hover:bg-slate-100"
                      }`}
                    >
                      <Icon className={`w-3 h-3 flex-shrink-0 ${isActive ? "text-blue-600" : "text-slate-400"}`} />
                      <span className={`text-[11px] font-medium truncate ${isActive ? "font-semibold" : ""}`}>
                        {item.label}
                      </span>
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Main Content */}
            <div className="flex-1 flex flex-col overflow-hidden">
              {/* Header */}
              <div className="h-12 border-b border-slate-100 flex items-center justify-between px-5 bg-white flex-shrink-0">
                <h3 className="font-bold text-slate-900 text-base">{view.title}</h3>
                <div className="flex items-center gap-2">
                  <span className="text-[10px] text-slate-400">This Month</span>
                  <span className="text-[10px] text-slate-400">|</span>
                  <span className="text-[10px] text-slate-400">All Providers</span>
                </div>
              </div>

              {/* KPIs */}
              <AnimatePresence mode="wait">
                <motion.div
                  key={activeView}
                  initial={{ opacity: 0, y: 8 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -8 }}
                  transition={{ duration: 0.3 }}
                  className="flex-1 flex flex-col overflow-hidden"
                >
                  <div className={`grid grid-cols-${view.kpis.length > 3 ? 4 : 3} gap-2.5 px-5 py-3 border-b border-slate-100`}>
                    {view.kpis.map((kpi, i) => (
                      <div
                        key={i}
                        className="bg-slate-50 rounded-xl p-2.5 border border-slate-100"
                      >
                        <p className="text-lg font-extrabold text-slate-900 tabular-nums">{kpi.value}</p>
                        <p className="text-[10px] text-slate-500 font-semibold">{kpi.label}</p>
                        <p className="text-[9px] text-slate-400">{kpi.sub}</p>
                      </div>
                    ))}
                  </div>

                  {/* AI Insights or Chart */}
                  <div className="flex-1 overflow-y-auto px-5 py-3">
                    {/* AI Insights Banner */}
                    <div className="flex items-center gap-2 mb-3 px-3 py-2 bg-blue-50 rounded-lg border border-blue-100">
                      <Sparkles className="w-3.5 h-3.5 text-blue-600" />
                      <span className="text-[11px] font-bold text-blue-700">AI Insights</span>
                    </div>

                    {view.insights && view.insights.map((insight, i) => {
                      const colorMap = {
                        green: { dot: "bg-green-500", bg: "bg-green-50", badge: "text-green-700 bg-green-100" },
                        amber: { dot: "bg-amber-500", bg: "bg-amber-50", badge: "text-amber-700 bg-amber-100" },
                        blue: { dot: "bg-blue-500", bg: "bg-blue-50", badge: "text-blue-700 bg-blue-100" },
                      };
                      const c = colorMap[insight.emoji] || colorMap.blue;
                      return (
                        <motion.div
                          key={i}
                          initial={{ opacity: 0, x: -10 }}
                          animate={{ opacity: 1, x: 0 }}
                          transition={{ delay: 0.2 + i * 0.15 }}
                          className="flex items-start gap-3 py-2.5 border-b border-slate-100 last:border-0"
                        >
                          <div className={`w-2 h-2 rounded-full ${c.dot} mt-1.5 flex-shrink-0`} />
                          <div className="flex-1">
                            <p className="text-sm font-semibold text-slate-800">{insight.title}</p>
                            <p className="text-xs text-slate-500 mt-0.5">{insight.detail}</p>
                          </div>
                        </motion.div>
                      );
                    })}

                    {view.chart && (
                      <div className="mt-2">
                        <p className="text-xs font-bold text-slate-500 uppercase tracking-wide mb-3">Production & Collections</p>
                        <div className="flex items-end gap-2 h-[140px]">
                          {["Jan", "Feb", "Mar", "Apr", "May", "Jun"].map((m, i) => {
                            const heights = [35, 45, 42, 55, 65, 80];
                            const collHeights = [30, 40, 38, 50, 58, 72];
                            return (
                              <div key={i} className="flex-1 flex flex-col items-center gap-0.5">
                                <div className="w-full flex items-end justify-center gap-0.5" style={{ height: "100%" }}>
                                  <motion.div
                                    className="w-[40%] bg-blue-400 rounded-t-sm"
                                    initial={{ height: 0 }}
                                    animate={{ height: `${heights[i]}%` }}
                                    transition={{ duration: 0.6, delay: i * 0.1 }}
                                  />
                                  <motion.div
                                    className="w-[40%] bg-blue-200 rounded-t-sm"
                                    initial={{ height: 0 }}
                                    animate={{ height: `${collHeights[i]}%` }}
                                    transition={{ duration: 0.6, delay: i * 0.1 + 0.05 }}
                                  />
                                </div>
                                <span className="text-[9px] font-bold text-slate-400">{m}</span>
                              </div>
                            );
                          })}
                        </div>
                        <div className="flex items-center gap-4 mt-2 pt-2 border-t border-slate-100">
                          <div className="flex items-center gap-1.5">
                            <div className="w-2.5 h-2.5 rounded-sm bg-blue-400" />
                            <span className="text-[10px] font-bold text-slate-500">Production</span>
                          </div>
                          <div className="flex items-center gap-1.5">
                            <div className="w-2.5 h-2.5 rounded-sm bg-blue-200" />
                            <span className="text-[10px] font-bold text-slate-500">Collections</span>
                          </div>
                        </div>
                      </div>
                    )}

                    {view.funnel && (
                      <div className="mt-2">
                        <p className="text-xs font-bold text-slate-500 uppercase tracking-wide mb-3">Treatment Funnel</p>
                        <div className="space-y-2">
                          {[
                            { label: "Diagnosed", value: "$705,813", width: "100%", color: "bg-blue-100 border-blue-200" },
                            { label: "Accepted", value: "$676,550", width: "85%", color: "bg-blue-200 border-blue-300" },
                            { label: "Scheduled", value: "$412,000", width: "58%", color: "bg-blue-400 border-blue-500 text-white" },
                            { label: "Completed", value: "$298,500", width: "42%", color: "bg-blue-600 border-blue-700 text-white" },
                          ].map((step, i) => (
                            <motion.div
                              key={i}
                              initial={{ opacity: 0, scaleX: 0 }}
                              animate={{ opacity: 1, scaleX: 1 }}
                              transition={{ delay: 0.2 + i * 0.15, duration: 0.4 }}
                              style={{ width: step.width, originX: 0 }}
                              className={`${step.color} border rounded-lg px-3 py-2 flex items-center justify-between`}
                            >
                              <span className="text-xs font-semibold">{step.label}</span>
                              <span className="text-xs font-bold tabular-nums">{step.value}</span>
                            </motion.div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                </motion.div>
              </AnimatePresence>

              {/* Bottom Bar */}
              <div className="h-10 border-t border-slate-100 bg-gradient-to-r from-blue-50 to-indigo-50 flex items-center justify-between px-5 flex-shrink-0">
                <div className="flex items-center gap-2">
                  <Sparkles className="w-3.5 h-3.5 text-blue-600" />
                  <span className="text-[10px] font-bold text-blue-700">AI-powered insights updated in real-time</span>
                </div>
                <span className="text-[10px] font-bold text-blue-600">AI Revenue Analytics Suite</span>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </div>
  );
};

export default PMSRevenueAnalyticsSlide;
